// Bootstrap 5 + BS3 compatibility bridges, loaded by every layout right after
// application.js (jQuery must already be loaded). CSS counterpart:
// bs3_compat.scss.
//
// Bootstrap's Button and ScrollSpy are intentionally not imported: BS3's
// stateful buttons are reimplemented in bs5/button-compat.js, and nothing
// uses scrollspy.
import Alert from 'bootstrap/js/dist/alert';
import Carousel from 'bootstrap/js/dist/carousel';
import Collapse from 'bootstrap/js/dist/collapse';
import Dropdown from 'bootstrap/js/dist/dropdown';
import Modal from 'bootstrap/js/dist/modal';
import Popover from 'bootstrap/js/dist/popover';
import Tab from 'bootstrap/js/dist/tab';
import Tooltip from 'bootstrap/js/dist/tooltip';

import { installDataAttributeBridge, upgradeElement } from '../bs5/data-attr-bridge';
import { installButtonCompat } from '../bs5/button-compat';
import { installMultiInstanceData } from '../bs5/multi-instance-data';
import { installLegacyInstanceBridge } from '../bs5/legacy-instance-bridge';

const components = { Alert, Carousel, Collapse, Dropdown, Modal, Popover, Tab, Tooltip };

window.bootstrap = Object.assign(window.bootstrap || {}, components);

// BS3 markup stacks components on one element (tooltip + dropdown) — allow it.
installMultiInstanceData();

// The app's BS3 build exposed a custom `reposition()` on tooltips/popovers
// (the former custom-bootstrap.js); with Popper this is just an update.
Tooltip.prototype.reposition = function () {
  this.update();
};

// BS3 only ever flipped tooltips/popovers vertically ('auto bottom' meant
// "bottom, or top if it doesn't fit") and slid them horizontally to stay in
// the viewport. Popper's default fallbacks flip to left/right too, which moves
// tips to places BS3 never put them (e.g. buttons near the right edge).
// Popper still shifts on the main axis, matching BS3's slide behavior.
// offset: BS3 put the tip bubble 8px from the trigger (3px placement margin +
// 5px border-drawn arrow); BS5's default [0, 6] reads visibly tighter. The
// matching 10x5px arrow lives in theme.scss ($tooltip-arrow-*).
// animation: BS5 keeps a hidden tip on screen for its 150ms fade-out, so tips
// linger and stack when you hover quickly between triggers. Show and hide
// them instantly instead.
Object.assign(Tooltip.Default, { fallbackPlacements: ['top', 'bottom'], offset: [0, 8], animation: false });
Object.assign(Popover.Default, { fallbackPlacements: ['top', 'bottom'] });
// BS3 positioned dropdown menus with fractional top/left; Popper rounds its
// offsets to whole pixels and applies them as translate3d, which lands menus up
// to .5px off the BS3 spot AND promotes them to a compositing layer (grayscale
// instead of subpixel text antialiasing). Keep Bootstrap's own modifiers
// (offset, preventOverflow); only change how the position is written.
Object.assign(Dropdown.Default, {
  popperConfig: (defaultConfig) => ({
    ...defaultConfig,
    modifiers: [
      ...defaultConfig.modifiers,
      { name: 'computeStyles', options: { roundOffsets: false, gpuAcceleration: false } },
    ],
  }),
});

const $ = window.jQuery;

if ($) {
  // Register the jQuery interfaces eagerly. Bootstrap's own defineJQueryPlugin
  // waits for DOMContentLoaded, but jQuery fires its ready callbacks first —
  // too late for legacy `$(...).modal(...)` calls living inside ready handlers.
  const jqueryPlugins = {
    alert: Alert,
    carousel: Carousel,
    collapse: Collapse,
    dropdown: Dropdown,
    modal: Modal,
    popover: Popover,
    tab: Tab,
    tooltip: Tooltip,
  };

  for (const [name, Component] of Object.entries(jqueryPlugins)) {
    $.fn[name] = function (config) {
      // Sync data-* -> data-bs-* mirror: BS5 snapshots data-bs-* config at
      // instantiation, and the observer-based mirror is async.
      this.each(function () { upgradeElement(this); });

      // BS3's teardown method was renamed in BS4+; BS5 throws on unknown
      // method names ("No method named destroy") — e.g. Vue dialogs tear down
      // their tooltips with .tooltip('destroy') on unmount.
      return Component.jQueryInterface.call(this, config === 'destroy' ? 'dispose' : config);
    };
    $.fn[name].Constructor = Component;
  }

  // BS3's `.modal()` / `.modal({...})` showed the modal by default
  // (Modal.DEFAULTS.show was true) — ajax_dialog and friends rely on it.
  // BS5's jQueryInterface only instantiates unless config.show is set.
  $.fn.modal = function (config, relatedTarget) {
    return this.each(function () {
      upgradeElement(this);
      if (typeof config === 'string') {
        Modal.jQueryInterface.call($(this), config === 'destroy' ? 'dispose' : config, relatedTarget);
      } else {
        const instance = Modal.getOrCreateInstance(this, config || {});

        if (!config || config.show !== false) {instance.show(relatedTarget);}
      }
    });
  };
  $.fn.modal.Constructor = Modal;

  installButtonCompat($);

  // Overrides $.fn.tooltip / $.fn.popover from the loop above so legacy code
  // reading `.data('bs.tooltip').$tip` etc. keeps working against BS5 instances.
  installLegacyInstanceBridge($, { Tooltip, Popover });
}

installDataAttributeBridge();
